import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaDownload, FaFilter } from 'react-icons/fa';
import Layout from './Layout';
import axios from 'axios';
import '../assets/css/SpProductCampaigns.css';

const SpProductCampaigns = () => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    // File state
    const [ownProductsFile, setOwnProductsFile] = useState(null);
    const [competitorFile, setCompetitorFile] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);

    // Filter state
    const [filters, setFilters] = useState({
        by_reviews: false,
        less_reviews_than_us: true,
        by_price: false,
        higher_price_than_us: true,
        by_rating: false,
        less_rating_than_us: true
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [analysis, setAnalysis] = useState(null);
    const [creationComplete, setCreationComplete] = useState(false);
    const [creationDiagnostics, setCreationDiagnostics] = useState(null);
    const [campaignState, setCampaignState] = useState('PAUSED');

    // Bulk actions states
    const [bulkAction, setBulkAction] = useState('');
    const [bulkBudget, setBulkBudget] = useState('');
    const [bulkStartDate, setBulkStartDate] = useState('');
    const [bulkEndDate, setBulkEndDate] = useState('');

    // Handle file uploads
    const handleOwnProductsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setOwnProductsFile(file);
        }
    };

    const handleCompetitorFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setCompetitorFile(file);
        }
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setNegativeFile(file);
        }
    };

    // Handle filter toggles
    const handleFilterChange = (filterName) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    // Generate campaigns
    const handleGenerateCampaigns = async () => {
        if (!ownProductsFile || !competitorFile) {
            setError('You must upload the own-products and competitors files');
            return;
        }

        setLoading(true);
        setError(null);
        setCreationDiagnostics(null);
        setResult(null);
        setAnalysis(null);
        setCreationDiagnostics(null);

        try {
            const formData = new FormData();
            formData.append('own_products_file', ownProductsFile);
            formData.append('competitor_file', competitorFile);

            if (negativeFile) {
                formData.append('negative_file', negativeFile);
            }

            // Add filter configuration
            formData.append('filters', JSON.stringify(filters));

            const response = await axios.post(`${API_URL}/sp-product-campaigns/generate`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const generatedCampaigns = response.data.campaigns || [];

                // Add selected field to each campaign row
                const campaignsWithSelection = generatedCampaigns.map(campaign => ({
                    ...campaign,
                    selected: true,
                    _uiBudget: campaign.Budget,
                    _uiStartDate: campaign['Start Date'] || campaign['Campaign Start Date'] || '',
                    _uiEndDate: campaign['End Date'] || campaign['Campaign End Date'] || ''
                }));

                setCampaigns(campaignsWithSelection);
                setAnalysis(response.data.analysis);

                setResult({
                    success: true,
                    message: `${generatedCampaigns.length} campaigns generated. Select which ones to create in Amazon.`,
                    count: generatedCampaigns.length
                });
            } else {
                setError(response.data.error || 'Error generating campaigns');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(err.response?.data?.error || err.message || 'Error generating campaigns');
        } finally {
            setLoading(false);
        }
    };

    // Create campaigns in Amazon
    const handleCreateInAmazon = async () => {
        const selectedCampaigns = campaigns.filter(c => c.selected);

        if (selectedCampaigns.length === 0) {
            setError('You must select at least one campaign to create in Amazon');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const profileId = localStorage.getItem('selectedAccountValue');

            if (!profileId) {
                setError('Please select an account in the selector above before creating campaigns');
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('own_products_file', ownProductsFile);
            formData.append('competitor_file', competitorFile);
            formData.append('profileId', profileId);
            formData.append('filters', JSON.stringify(filters));

            if (negativeFile) {
                formData.append('negative_file', negativeFile);
            }

            const selectedIndices = (campaigns || []).map((c, idx) => c.selected ? idx : -1)
                .filter(idx => idx !== -1);
            formData.append('selected_campaign_indices', JSON.stringify(selectedIndices));
            formData.append('selected_campaigns_data', JSON.stringify(selectedCampaigns));
            formData.append('campaignState', campaignState);

            const response = await axios.post(`${API_URL}/sp-product-campaigns/create-in-amazon`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const amazonResults = response.data.results || [];
            const errors = response.data.errors || [];
            const debugTrace = response.data.debugTrace || [];
            const campaignsCreated = response.data?.campaigns_created || 0;
            const campaignsFailed = response.data?.campaigns_failed || 0;
            const failureReasons = getFailureReasons(errors, debugTrace);

            setCreationDiagnostics({
                requestId: response.data?.requestId || null,
                campaignsCreated,
                campaignsFailed,
                errors,
                debugTrace,
                failureReasons,
                success: !!response.data.success,
            });

            setResult({
                success: !!response.data.success,
                message: response.data.success
                    ? `Created ${campaignsCreated} campaigns in Amazon. ${campaignsFailed} errors.`
                    : `Campaign creation finished with issues. Created ${campaignsCreated}, failed ${campaignsFailed}.`,
                count: campaignsCreated,
                amazonResults: amazonResults,
                errors: errors
            });

            if (response.data.success) {

                const sourceCampaignsByName = new Map(
                    selectedCampaigns.map((campaign) => [campaign['Campaign Name'], campaign])
                );

                const campaignsForTable = amazonResults.map((result, idx) => {
                    const sourceCampaign = sourceCampaignsByName.get(result.campaignName) || selectedCampaigns[idx] || {};

                    return {
                        'Campaign Name': result.campaignName,
                        'Campaign ID': result.campaignId,
                        'Ad Group ID': result.adGroupId,
                        'Product Ads': result.productAdsCount,
                        'Target ASINs': result.targetASINsCount,
                        ASINs: result.asins || sourceCampaign.ASINs || sourceCampaign.ASIN || '-',
                        Bid: result.bid ?? sourceCampaign.Bid ?? '-',
                        Budget: result.budget ?? sourceCampaign.Budget ?? '-',
                        'Start Date': result.startDate || sourceCampaign['Start Date'] || sourceCampaign['Campaign Start Date'] || '-',
                        'End Date': result.endDate || sourceCampaign['End Date'] || sourceCampaign['Campaign End Date'] || '-',
                        _uiBudget: result.budget ?? sourceCampaign._uiBudget ?? sourceCampaign.Budget ?? '-',
                        _uiStartDate: result.startDate || sourceCampaign._uiStartDate || sourceCampaign['Start Date'] || sourceCampaign['Campaign Start Date'] || '-',
                        _uiEndDate: result.endDate || sourceCampaign._uiEndDate || sourceCampaign['End Date'] || sourceCampaign['Campaign End Date'] || '-',
                        'Price Ratio': sourceCampaign['Price Ratio'] ?? '-',
                        selected: false,
                        'Status': 'Created in Amazon'
                    };
                });

                setCampaigns(campaignsForTable);
                setCreationComplete(true);
            } else {
                const firstReason = failureReasons?.[0];
                setError(firstReason || response.data.error || 'Error creating campaigns in Amazon');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(err.response?.data?.error || err.message || 'Error creating campaigns in Amazon');
            setCreationDiagnostics({
                requestId: err.response?.data?.requestId || null,
                campaignsCreated: err.response?.data?.campaigns_created || 0,
                campaignsFailed: err.response?.data?.campaigns_failed || 0,
                errors: err.response?.data?.errors || [],
                debugTrace: err.response?.data?.debugTrace || [],
                failureReasons: getFailureReasons(
                    err.response?.data?.errors || [],
                    err.response?.data?.debugTrace || [],
                ),
                success: false,
            });
        } finally {
            setLoading(false);
        }
    };

    // Toggle selection
    const toggleCampaignSelection = (index) => {
        const updatedCampaigns = [...campaigns];
        updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
        setCampaigns(updatedCampaigns);
    };

    const selectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: true }));
        setCampaigns(updatedCampaigns);
    };

    const deselectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: false }));
        setCampaigns(updatedCampaigns);
    };

    // Apply bulk action
    const applyBulkAction = () => {
        if (!bulkAction) {
            setError('Select a bulk action');
            return;
        }

        if (bulkAction === 'budget' && (!bulkBudget || parseFloat(bulkBudget) <= 0)) {
            setError('Enter a valid budget greater than 0');
            return;
        }

        if (bulkAction === 'startDate' && !bulkStartDate) {
            setError('Select a valid start date');
            return;
        }

        if (bulkAction === 'endDate' && !bulkEndDate) {
            setError('Select a valid end date');
            return;
        }

        const selectedCount = campaigns.filter(c => c.selected).length;
        const applyToAllCampaigns = selectedCount === 0;
        const affectedCount = applyToAllCampaigns ? campaigns.length : selectedCount;

        if (affectedCount === 0) {
            setError('No campaigns available to update');
            return;
        }

        const updatedCampaigns = campaigns.map(campaign => {
            if (!applyToAllCampaigns && !campaign.selected) return campaign;

            const updated = { ...campaign };

            switch (bulkAction) {
                case 'budget':
                    if (bulkBudget && parseFloat(bulkBudget) > 0) {
                        const parsedBudget = parseFloat(bulkBudget);
                        updated.Budget = parsedBudget;
                        updated._uiBudget = parsedBudget;
                    }
                    break;
                case 'startDate':
                    if (bulkStartDate) {
                        updated['Start Date'] = bulkStartDate;
                        updated['Campaign Start Date'] = bulkStartDate;
                        updated._uiStartDate = bulkStartDate;
                    }
                    break;
                case 'endDate':
                    if (bulkEndDate) {
                        updated['End Date'] = bulkEndDate;
                        updated['Campaign End Date'] = bulkEndDate;
                        updated._uiEndDate = bulkEndDate;
                    }
                    break;
                default:
                    break;
            }

            return updated;
        });

        setCampaigns(updatedCampaigns);
        setBulkAction('');
        setBulkBudget('');
        setBulkStartDate('');
        setBulkEndDate('');
        setError(null);
        setResult({
            success: true,
            message: applyToAllCampaigns
                ? `Action applied to all ${affectedCount} campaign(s)`
                : `Action applied to ${affectedCount} campaign(s)`,
            count: affectedCount
        });
    };

    const formatDateCell = (value) => {
        if (!value) return '-';
        const asString = String(value);
        if (asString.includes('T')) {
            return asString.slice(0, 10);
        }
        return asString;
    };

    const formatBudgetCell = (value) => {
        if (value === undefined || value === null || value === '' || value === '-') return '-';
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
            return `$${numericValue.toFixed(2)}`;
        }
        return String(value);
    };

    const getFailureReasons = (errors = [], trace = []) => {
        const fromErrors = (errors || [])
            .flatMap((entry) => entry?.errors || [])
            .filter(Boolean);

        const fromTrace = (trace || [])
            .filter((item) => !item?.success && Array.isArray(item?.errors))
            .flatMap((item) => item.errors)
            .filter(Boolean);

        return Array.from(new Set([...fromErrors, ...fromTrace]));
    };

    // Download CSV
    const handleDownloadCampaigns = () => {
        if (campaigns.length === 0) return;

        const headers = Object.keys(campaigns[0]);
        const csvContent = [
            headers.join(','),
            ...campaigns.map(campaign =>
                headers.map(header => {
                    const value = campaign[header];
                    if (typeof value === 'string' && value.includes(',')) {
                        return `"${value}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `sp_product_campaigns_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Reset
    const handleReset = () => {
        setOwnProductsFile(null);
        setCompetitorFile(null);
        setNegativeFile(null);
        setCampaigns([]);
        setResult(null);
        setError(null);
        setAnalysis(null);
        setCreationComplete(false);
        setCreationDiagnostics(null);
    };

    return (
        <Layout>
            <Container fluid className="sp-product-campaigns-page py-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className="text-white mb-3">
                            <FaRocket className="me-2" />
                            Sponsored Products - Product Campaigns
                        </h2>
                        <p className="text-muted">
                            Generate SP campaigns that target competitor products using your ASINs
                        </p>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {result?.success && result?.message && (
                    <Alert variant="success" className="mb-3">
                        {result.message}
                    </Alert>
                )}

                {/* Upload Section */}
                {!result && (
                    <>
                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Card 1: Own Products */}
                            <div className={`upload-card-new ${ownProductsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Own Products CSV</div>
                                <div className="upload-card-desc">
                                    Columns: ASIN, SKU. Optional: Price, Rating, Reviews, Abbreviation
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {ownProductsFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleOwnProductsFileChange} hidden />
                                </label>
                                {ownProductsFile && <small className="text-success mt-2">{ownProductsFile.name}</small>}
                            </div>

                            {/* Card 2: Competitors */}
                            <div className={`upload-card-new ${competitorFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Competitors CSV</div>
                                <div className="upload-card-desc">
                                    Columns: ASIN. Optional: Price, Rating, Reviews
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {competitorFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleCompetitorFileChange} hidden />
                                </label>
                                {competitorFile && <small className="text-success mt-2">{competitorFile.name}</small>}
                            </div>

                            {/* Card 3: Negative Targeting */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Targeting (Optional)</div>
                                <div className="upload-card-desc">
                                    Columns: Abbreviation, Negative Targeting, Match Type
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {negativeFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>
                        </div>

                        {/* Filters Section */}
                        <div className="config-section-card">
                            <h5 className="mb-3">
                                <FaFilter className="me-2 text-primary" />
                                Competitive Filters (Optional)
                            </h5>
                            <p className="text-muted small mb-3">
                                Filter competitors to target only those where you have a competitive advantage
                            </p>
                            <Row>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-reviews" label="By Reviews: Fewer reviews than us"
                                        checked={filters.by_reviews}
                                        onChange={() => handleFilterChange('by_reviews')}
                                        className="mb-2"
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-price" label="By Price: Higher price than us"
                                        checked={filters.by_price}
                                        onChange={() => handleFilterChange('by_price')}
                                        className="mb-2"
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-rating" label="By Rating: Lower rating than us"
                                        checked={filters.by_rating}
                                        onChange={() => handleFilterChange('by_rating')}
                                        className="mb-2"
                                    />
                                </Col>
                            </Row>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button className={`btn-generate-campaigns ${(!loading && ownProductsFile && competitorFile) ? 'ready' : ''}`}
                                onClick={handleGenerateCampaigns}
                                disabled={loading || !ownProductsFile || !competitorFile}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Generating campaigns...
                                    </>
                                ) : (
                                    <>
                                        <FaRocket />
                                        Generate Campaigns
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* Analysis Section */}
                {analysis && (
                    <Alert variant="info" className="mb-3">
                        <strong>📊 Analysis:</strong> {analysis.own_products_count} own products | {analysis.filtered_competitors_count} filtered competitors |
                        Price ratio: {analysis.price_ratio} | Base bid: ${analysis.base_bid} | {analysis.target_asins_per_campaign} ASIN targets per campaign
                    </Alert>
                )}

                {analysis?.warnings?.length > 0 && (
                    <Alert variant="warning" className="mb-3">
                        <strong>CSV validation warnings:</strong>
                        <ul className="mb-0 mt-1 ps-3">
                            {analysis.warnings.map((warning, idx) => (
                                <li key={`csv-warning-${idx}`}>{warning}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {creationDiagnostics && (
                    <Alert
                        variant={creationDiagnostics.campaignsFailed > 0 ? 'warning' : 'success'}
                        className="mb-4"
                    >
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <strong>Amazon creation diagnostics</strong>
                            <small>Request ID: {creationDiagnostics.requestId || 'n/a'}</small>
                        </div>
                        <div className="mb-2">
                            Created: {creationDiagnostics.campaignsCreated} | Failed: {creationDiagnostics.campaignsFailed}
                        </div>
                        {creationDiagnostics.failureReasons?.length > 0 && (
                            <div className="mb-2 small">
                                <strong>Failure reasons:</strong>
                                <ul className="mb-0 mt-1 ps-3">
                                    {creationDiagnostics.failureReasons.map((reason, idx) => (
                                        <li key={`reason-${idx}`}>{reason}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {creationDiagnostics.debugTrace?.length > 0 && (
                            <div className="small">
                                {creationDiagnostics.debugTrace.map((item, idx) => (
                                    <div key={`${item.campaignName || 'campaign'}-${idx}`} className="mb-2 p-2 border rounded">
                                        <div>
                                            <strong>{item.campaignName || `Campaign ${idx + 1}`}</strong>
                                            <Badge bg={item.success ? 'success' : 'danger'} className="ms-2">
                                                {item.success ? 'Success' : 'Failed'}
                                            </Badge>
                                        </div>
                                        {item.success && (
                                            <div className="mt-1">
                                                Campaign ID: {item.campaignId || 'n/a'} | Ad Group ID: {item.adGroupId || 'n/a'} | Product Ads: {item.productAdsCount || 0}
                                            </div>
                                        )}
                                        {!item.success && Array.isArray(item.errors) && item.errors.length > 0 && (
                                            <ul className="mb-0 mt-1 ps-3">
                                                {item.errors.map((detail, detailIdx) => (
                                                    <li key={`${idx}-${detailIdx}`}>{detail}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Alert>
                )}

                {/* Results Section */}
                {result && campaigns.length > 0 && (
                    <Row>
                        <Col>
                            <Card className="config-section-card border-0 p-0 overflow-hidden">
                                <Card.Header className="bg-dark text-white p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><FaRocket className="me-2" />Generated Campaigns ({campaigns.length})</h5>
                                    </div>

                                    <div>
                                        {/* Bulk Actions Row */}
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <div className="d-flex gap-2 align-items-center">
                                                <span className="text-white fw-bold">Bulk actions:</span>
                                                <Form.Select
                                                    size="sm"
                                                    value={bulkAction}
                                                    onChange={(e) => setBulkAction(e.target.value)}
                                                    style={{ width: '200px' }}
                                                >
                                                    <option value="">Select action</option>
                                                    <option value="budget">Set budget...</option>
                                                    <option value="startDate">Set Start Date...</option>
                                                    <option value="endDate">Set End Date...</option>
                                                </Form.Select>

                                                {bulkAction === 'budget' && (
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        placeholder="New budget"
                                                        value={bulkBudget}
                                                        onChange={(e) => setBulkBudget(e.target.value)}
                                                        style={{ width: '150px' }}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                )}

                                                {bulkAction === 'startDate' && (
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={bulkStartDate}
                                                        onChange={(e) => setBulkStartDate(e.target.value)}
                                                        style={{ width: '150px' }}
                                                    />
                                                )}

                                                {bulkAction === 'endDate' && (
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={bulkEndDate}
                                                        onChange={(e) => setBulkEndDate(e.target.value)}
                                                        style={{ width: '150px' }}
                                                    />
                                                )}

                                                {bulkAction && (
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={applyBulkAction}
                                                    >
                                                        Apply
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons Row */}
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex gap-2 align-items-center">
                                                <Form.Select
                                                    size="sm"
                                                    value={campaignState}
                                                    onChange={(e) => setCampaignState(e.target.value)}
                                                    style={{ width: 'auto' }}
                                                    disabled={creationComplete}
                                                >
                                                    <option value="ENABLED">Create as: Enabled</option>
                                                    <option value="PAUSED">Create as: Paused</option>
                                                </Form.Select>

                                                <Button variant="outline-success" size="sm" onClick={handleDownloadCampaigns}>
                                                    <FaDownload className="me-1" /> Download CSV
                                                </Button>
                                                <Button variant="outline-primary" size="sm" onClick={handleReset}>
                                                    <FaRocket className="me-1" /> New Generation
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0 table-responsive bg-dark" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover variant="dark" className="align-middle mb-0" size="sm" style={{ fontSize: '0.85rem' }}>
                                        <thead className="text-warning sticky-top bg-dark">
                                            <tr>
                                                <th width="40">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={campaigns.every(c => c.selected)}
                                                        onChange={(e) => e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()}
                                                    />
                                                </th>
                                                <th width="50">#</th>
                                                <th width="250">Campaign Name</th>
                                                <th width="100">ASIN</th>
                                                <th width="80">Bid</th>
                                                <th width="80">Budget</th>
                                                <th width="120">Start Date</th>
                                                <th width="120">End Date</th>
                                                <th width="80">Targets</th>
                                                <th width="100">Price Ratio</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={campaign.selected || false}
                                                            onChange={() => toggleCampaignSelection(idx)}
                                                        />
                                                    </td>
                                                    <td>{idx + 1}</td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '230px' }}>
                                                            {campaign['Campaign Name']}
                                                        </div>
                                                    </td>
                                                    <td className="text-info">{campaign.ASINs || campaign.ASIN}</td>
                                                    <td>${campaign.Bid}</td>
                                                    <td>{formatBudgetCell(campaign._uiBudget ?? campaign.Budget)}</td>
                                                    <td>{formatDateCell(campaign._uiStartDate || campaign['Start Date'] || campaign['Campaign Start Date'])}</td>
                                                    <td>{formatDateCell(campaign._uiEndDate || campaign['End Date'] || campaign['Campaign End Date'])}</td>
                                                    <td>
                                                        <Badge bg="primary">{campaign['Target Count'] ?? campaign['Target ASINs'] ?? 0} ASINs</Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={campaign['Price Ratio'] < 1 ? 'success' : campaign['Price Ratio'] > 1 ? 'warning' : 'secondary'}>
                                                            {campaign['Price Ratio'] ? `${campaign['Price Ratio']}x` : '-'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                                    <div className="d-flex gap-2">
                                        <Button variant="secondary" onClick={handleReset} className="fw-bold">← Cancel</Button>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="text-muted small">
                                            Total: {campaigns.length} | Selected: {campaigns.filter(c => c.selected).length}
                                        </div>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleCreateInAmazon}
                                            disabled={loading || creationComplete || campaigns.filter(c => c.selected).length === 0}
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '6px 20px'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Creating in Amazon...
                                                </>
                                            ) : creationComplete ? (
                                                <>
                                                    <FaCheckCircle className="me-1" /> Campaigns Created
                                                </>
                                            ) : (
                                                <>
                                                    Create Selected Campaigns
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Requirements Section */}
                {!result && (
                    <div className="requirements-section">
                        <span className="requirements-title">Requirements - CSV Files</span>
                        <div className="req-item"><strong>Own Products CSV:</strong> Must include 'ASIN', 'SKU'. Optional: Price, Rating, Reviews, Abbreviation.</div>
                        <div className="req-item"><strong>Competitors CSV:</strong> Must include 'ASIN'. Optional: Price, Rating, Reviews for filters.</div>
                        <div className="req-item"><strong>Negative Targeting CSV:</strong> Must include 'Abbreviation', 'Negative Targeting', 'Match Type'.</div>
                    </div>
                )}
            </Container>
        </Layout>
    );
};

export default SpProductCampaigns;



